import debounce from 'lodash.debounce'
import { defineStore } from 'pinia'
import { ref, watch, computed } from 'vue'

import { useApi } from '@/shared/composables/api'
import { usePaginatedData } from '@/shared/composables/use-paginated-data'
import {
  Contact,
  GetContactsQuery,
  Response,
  WithPagination,
} from '@/shared/types/api'
import { GroupsTypes } from '@/shared/types/groups'
import { mergeSystemBotSearchResults } from '@/shared/utils/contacts'
import sort from '@/shared/utils/sort'

import { useStoreContacts } from '..'

type ContactsApiFilter = NonNullable<GetContactsQuery['Filters']>[number]

export const useStoreContactsFilterable = defineStore('contacts-filterable', () => {
  const contactsStore = useStoreContacts()

  const search = ref('')
  const searchForFilter = ref(search.value)
  const group = ref<GroupsTypes>(GroupsTypes.ALL)
  const contactType = ref<'ALL' | 'USER_CONTACTS' | 'FAVORITES'>('ALL')
  const isLoading = ref(false)

  const filters = computed<Partial<GetContactsQuery>>(() => {
    const result: Partial<GetContactsQuery> = {}
    const searchVal = searchForFilter.value
    const isDigitsOnly = Boolean(searchVal && /^\d+$/.test(searchVal))

    if (searchVal && !isDigitsOnly) {
      result.Filters = [{
        AvailableOperations: ['contains'],
        AvailableValues: searchVal,
        PropertyName: 'names',
        TypeFilter: 'string',
      }]
    }

    if (group.value !== GroupsTypes.ALL) {
      result.groupId = group.value
    }

    if (contactType.value === 'USER_CONTACTS') {
      result.contactType = 'externalContact'
      result.groupType = 'user'
    }

    if (contactType.value === 'FAVORITES' && !isDigitsOnly) {
      result.Filters = [
        ...(result.Filters ?? []),
        {
          AvailableOperations: ['eq'],
          AvailableValues: 'true',
          PropertyName: 'isFavorite',
          TypeFilter: 'string',
        },
      ]
    }

    return result
  })

  const filtersIsEnabled = computed(() => {
    const searchVal = searchForFilter.value
    if (searchVal && /^\d+$/.test(searchVal)) {
      return true
    }
    return !!Object.keys(filters.value).length
  })

  const alphabeticallySortedList = computed(() => {
    const sorted = [...list.value].sort(sort.contacts.byFavoriteThenName)
    const query = searchForFilter.value
    if (!query || /^\d+$/.test(query)) {
      return sorted
    }

    return mergeSystemBotSearchResults(query, sorted)
  })

  let controller: AbortController | undefined
  const { list, fetchNext, reset } = usePaginatedData<Contact>({
    size: 40,
    idKey: 'id',
    fetchData: async (data: GetContactsQuery) => {
      if (controller) {
        controller.abort()
      }

      controller = new AbortController()
      isLoading.value = true

      const searchVal = searchForFilter.value
      const isDigitsOnly = Boolean(searchVal && /^\d+$/.test(searchVal))

      /** Два фильтра в одном запросе без поддержки OR на бэкенде дают AND — как fetchContactsByNameOrNumber */
      if (isDigitsOnly) {
        const needle = searchVal as string
        const favoriteOnly = contactType.value === 'FAVORITES'

        const favoriteFilter = {
          AvailableOperations: ['eq'],
          AvailableValues: 'true',
          PropertyName: 'isFavorite',
          TypeFilter: 'string',
        } as ContactsApiFilter

        let internalFilters: NonNullable<GetContactsQuery['Filters']>
        let mobileFilters: NonNullable<GetContactsQuery['Filters']>

        if (favoriteOnly) {
          internalFilters = [
            {
              AvailableOperations: ['contains'],
              AvailableValues: needle,
              PropertyName: 'internalNumber',
              TypeFilter: 'phone',
            },
            favoriteFilter,
          ]
          mobileFilters = [
            {
              AvailableOperations: ['contains'],
              AvailableValues: needle,
              PropertyName: 'mobilePhone',
              TypeFilter: 'phone',
            },
            favoriteFilter,
          ]
        } else {
          internalFilters = [{
            AvailableOperations: ['contains'],
            AvailableValues: needle,
            PropertyName: 'internalNumber',
            TypeFilter: 'phone',
          }]
          mobileFilters = [{
            AvailableOperations: ['contains'],
            AvailableValues: needle,
            PropertyName: 'mobilePhone',
            TypeFilter: 'phone',
          }]
        }

        const base: GetContactsQuery = {
          Page: 1,
          Size: 1000,
          SortBy: data.SortBy ?? 'name',
        }

        if (group.value !== GroupsTypes.ALL) {
          base.groupId = group.value
        }

        if (contactType.value === 'USER_CONTACTS') {
          base.contactType = 'externalContact'
          base.groupType = 'user'
        }

        try {
          const api = useApi()
          const [internalRes, mobileRes] = await Promise.all([
            api.getContacts({ ...base, Filters: internalFilters }, controller),
            api.getContacts({ ...base, Filters: mobileFilters }, controller),
          ])

          isLoading.value = false

          const seen = new Map<string, Contact>()
          for (const c of internalRes?.data?.list ?? []) {
            seen.set(c.id, c)
          }
          for (const c of mobileRes?.data?.list ?? []) {
            seen.set(c.id, c)
          }

          const mergedList = [...seen.values()].sort(sort.contacts.byFavoriteThenName)
          mergedList.forEach(c => contactsStore.setContactIfDoesNotExists(c))

          const total = mergedList.length > 0 ? mergedList.length : 1

          return {
            data: {
              list: mergedList,
              page: {
                total,
                lastPage: 1,
                currentPage: 1,
                perPage: mergedList.length || data.Size,
              },
            },
          } as Response<WithPagination<Contact[]>>
        } catch (e: unknown) {
          const err = e as { code?: string }
          if (err.code !== 'ERR_CANCELED') {
            isLoading.value = false
          }
        }

        return
      }

      return useApi().getContacts({
        ...data,
        ...filters.value,
      }, controller)
        .then(response => {
          isLoading.value = false

          response.data.list.forEach(c => contactsStore.setContactIfDoesNotExists(c))

          return response
        })
        .catch(e => {
          if (e.code !== 'ERR_CANCELED') {
            isLoading.value = false
          }
        })
    },
  })

  const addOrUpdateContact = (contact: Contact) => {
    if (filtersIsEnabled.value) {
      const index = list.value.findIndex(c => c.guid === contact.guid)
      if (contactType.value === 'FAVORITES' && !contact.isFavorite) {
        if (index !== -1) {
          list.value.splice(index, 1)
        }
        return
      }

      if (index !== -1) {
        list.value.splice(index, 1, contact)
      } else {
        list.value.push(contact)
      }
    }
  }

  const deleteContactFromList = (contact: Contact) => {
    if (filtersIsEnabled.value) {
      list.value = list.value.filter(c => c.guid !== contact.guid)
    }
  }

  watch(search, debounce((v) => {
    searchForFilter.value = v
  }, 500))

  watch(filters, () => {
    reset()

    if (filtersIsEnabled.value) {
      isLoading.value = false
      fetchNext()
    }
  })

  return {
    search,
    group,
    contactType,
    isLoading,
    filters,
    filtersIsEnabled,
    alphabeticallySortedList,
    fetchNext,
    addOrUpdateContact,
    deleteContactFromList,
  }
})
